import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import { checkTabIsActive, PageHeading, Spinner, Tabs } from "@prequel/shared-components";

import SourceForm from "../SourceForm";

import { useSources } from "@/api/queries/sources";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import BufoPage from "@/pages/BufoPage";
import { isBufoUUID } from "@/pages/BufoPage/bufo";
import ExistingSource from "@/types/sources";

type SourceContext = {
  source: ExistingSource;
};

// Parent wrapper: Loads data once, provides via context
const SourcePage = () => {
  const sourceID = useRequiredParam("sourceID");
  const { data: sources } = useSources();

  // Find the specific source by ID
  const source = sources?.find((s) => s.id === sourceID) ?? (sources === undefined ? undefined : null);

  if (source === undefined) {
    // Still loading connections
    return <Spinner />;
  }

  if (source === null) {
    if (isBufoUUID(sourceID)) {
      // Easter egg shows page with random bufo
      return <BufoPage />;
    }

    // Connections loaded, but could not find what we're looking for
    return <Navigate to="/sources" replace />;
  }

  return <Outlet context={{ source }} />;
};

// Details page with tabs
export const SourceDetailsPage = () => {
  const navigate = useNavigate();
  const { source } = useOutletContext<SourceContext>();
  const sourceID = source.id;

  const tabs = [
    <NavLink key="General" to={`/sources/${sourceID}`} className={checkTabIsActive} end>
      General
    </NavLink>,
    <NavLink key="Connection" to={`/sources/${sourceID}/connection`} className={checkTabIsActive} end>
      Connection
    </NavLink>,
  ];

  return (
    <>
      <div className="max-w-4xl px-7 py-4">
        <PageHeading
          title={source.name}
          onGoBack={() => navigate("/sources")}
          breadcrumbs={<Breadcrumbs dynamicLabel={source.name} />}
        />
        <div className="flex-1">
          <div className="relative mx-auto max-w-4xl">
            <div className="pt-0 pb-16">
              <div className="pb-6">
                {/* Tabs */}
                <Tabs tabs={tabs} />
                <Outlet context={{ source }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Edit page: Full page component, gets data from context
export const SourceEditPage = () => {
  const navigate = useNavigate();
  const { source } = useOutletContext<SourceContext>();
  const sourceID = source.id;

  return (
    <div className="max-w-lg px-7 py-4">
      <PageHeading
        title="Edit Source"
        onGoBack={() => navigate(`/sources/${sourceID}`)}
        breadcrumbs={<Breadcrumbs dynamicLabel={source.name} />}
      />
      <SourceForm />
    </div>
  );
};

export default SourcePage;
